<template>
  <div
    class="timeline"
    :style="{
      width: width + 'px',
      height: height + 'px'
    }"
  >
    <!-- Main timeline -->
    <div
      ref="timeline"
      class="timeline__thumbnails thumbnails"
      @mousemove="handleMouseMove"
      @mouseleave="$emit('mouseleave')"
      @mouseenter="$emit('mouseenter')"
      @dblclick="handleMovePlayer"
    >
      <!-- live pointer -->
      <timeline-cursor
        v-if="isStreamEndVisible"
        ref="liveCursor"
        color="pink"
      />
      <!-- player pointer -->
      <timeline-cursor
        v-if="isPlayerVisible"
        ref="playerCursor"
        color="yellow"
      />

      <!-- thumbnails -->
      <timeline-thumbnails
        v-if="hasThumbnails && showThumbnails == true"
        class="thumbnails__list"
        :timerange="timerange"
        :thumbnail-size="thumbnailSize"
        :timestep="thumbnailDuration"
      />

      <!-- trim slider -->
      <timeline-range-slider
        v-if="slider"
        class="thumbnails__slider"
        :borders="timerange"
        :paddings="sliderPaddings"
        :value-checkbox-range="valueCheckboxRange"
        :slider-left="slider[0]"
        :slider-right="slider[1]"
        :is-smooth-moving="isSliderSmooth"
        @changing="$emit('sliderDragging', $event)"
        @endChange="$emit('sliderDraggingStop')"
        @rangeChanged="$emit('sliderMoved', $event)"
      />

      <!-- breaks -->
      <timeline-breaks-area
        class="thumbnails__breaks"
        :breaks="breaks"
        :start="timerange[0]"
        :end="timerange[1]"
        :scale-mode="scaleMode"
      />
    </div>
  </div>
</template>

<script>
import TimelineThumbnails from "@/components/timeline/TimelineThumbnails.vue";
import TimelineCursor from "@/components/timeline/TimelineCursor.vue";
import TimelineRangeSlider from "@/components/timeline/TimelineRangeSlider.vue";
import TimelineBreaksArea from "@/components/timeline/TimelineBreaksArea.vue";

import { SCALE_MODE } from "@/constants.js";
import { mapGetters, mapState } from "vuex";
import { roundToMilliseconds } from "@/utils/time.js";

// @group Timeline
// Piece of timeline
export default {
  name: "TimelineFrame",
  components: {
    TimelineThumbnails,
    TimelineCursor,
    TimelineRangeSlider,
    TimelineBreaksArea
  },
  props: {
    valueCheckboxRange: {
      type: Boolean,
      default: false
    },
    showThumbnails: {
      type: Boolean,
      default: true
    },
    /** start & end UNIX timestamps of frame */
    timerange: {
      type: Array,
      default: () => [0, 1],
      validator([start, end]) {
        return start < end;
      }
    },
    /** Stream last recorded timestamp */
    streamStart: {
      type: Number,
      default: null
    },
    /** Stream live-point timestamp */
    streamEnd: {
      type: Number,
      default: null
    },
    /** Current player position timestamp */
    playerTime: {
      type: Number,
      default: null
    },
    /** Frame width in pixels */
    width: {
      type: Number,
      default: 800
    },
    /** Frame height in pixels */
    height: {
      type: Number,
      default: 130
    },
    /** Trim slider left & right bounds timestamps */
    slider: {
      type: Array,
      default: null,
      validator(val) {
        return val === null || val.length == 2;
      }
    },
    /** To animate slider motion not-linear */
    isSliderSmooth: {
      type: Boolean,
      default: true
    },
    /** Break ranges array */
    breaks: {
      type: Array,
      default: () => []
    },
    /** is thumbnails on timeline or not */
    hasThumbnails: {
      type: Boolean,
      default: true
    },
    /** how near timeline zoomed */
    scaleMode: {
      type: String,
      default: SCALE_MODE.NORMAL
    },
    /** thumbnail width & height */
    thumbnailSize: {
      type: Object,
      required: true
    },
    /** thumbnail time step */
    thumbnailDuration: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      /** X mouse position relative to timeline start */
      mousePosition: 0,
      playerPosition: null,
      playerMotionDuration: 0
    };
  },
  computed: {
    ...mapState({
      timezone: state => state.timezone.timezone
    }),
    ...mapGetters("thumbnails", ["getThumbnailLink"]),
    // @vuese
    // Frame start timestamp
    start() {
      return this.timerange[0];
    },
    // @vuese
    // Frame end timestamp
    end() {
      return this.timerange[1];
    },
    // @vuese
    // Frame duration in seconds
    duration() {
      return this.end - this.start;
    },

    // @vuese
    // Position of mouse relative to timeline start in range 0-1
    mouseRelativePosition() {
      return this.mousePosition / this.width;
    },
    // @vuese
    // mouse time from timeline frame start time
    mouseRelativeTime() {
      return roundToMilliseconds(this.duration * this.mouseRelativePosition);
    },
    // @vuese
    // Absolute mouse time timestamp
    mouseTime() {
      return this.mouseRelativeTime + this.start;
    },

    // @vuese
    // Is stream's live-point in frame range
    isStreamEndVisible() {
      return this.checkTimeVisible(this.streamEnd);
    },
    // @vuese
    // Stream live-point position on frame as (left padding / full width) ratio (in range [0-1])
    streamEndPosition() {
      return this.getTimePosition(this.streamEnd);
    },
    // @vuese
    // Is player time in frame range
    isPlayerVisible() {
      return this.checkTimeVisible(this.playerTime);
    },
    // @vuese
    // Start & end trim slider paddings from borders
    sliderPaddings() {
      // return [0, 0];
      const leftDiff =
        this.streamStart == null
          ? 0
          : roundToMilliseconds(this.streamStart - this.timerange[0]);
      const rightDiff =
        this.streamEnd == null
          ? 0
          : roundToMilliseconds(this.timerange[1] - this.streamEnd);

      return [Math.max(0, leftDiff), Math.max(0, rightDiff)];
    }
  },
  watch: {
    // @vuese
    // Update mouse time
    mousePosition() {
      /** mouse moved to time */
      this.$emit("mouseMovedToTime", this.mouseTime);
    },
    // @vuese
    // Update player position when render range is changing
    timerange() {
      this.syncPlayerCursor();
    },
    // @vuese
    // Render live pointer when it appers in render range
    isStreamEndVisible() {
      if (this.isStreamEndVisible) {
        this.$nextTick(() => this.syncLiveCursor({ smooth: true }));
      }
    },
    // @vuese
    // Update player position when player time is changing
    playerTime() {
      this.syncPlayerCursor({ smooth: true });
    }
  },
  mounted() {
    this.syncLiveCursor();
    this.syncPlayerCursor();
  },
  methods: {
    // @vuese
    // Click handler that notify parent about user tried to move
    // player cursor by click and transmits target time
    // @arg event - mouse event
    handleMovePlayer(event) {
      const { target, pageX } = event;

      if (
        target.classList.contains("noUi-handle") ||
        target.classList.contains("timeline-button")
      ) {
        return;
      }
      const leftOffset = this.$refs.timeline.getBoundingClientRect().left;
      const clickPosition = pageX - leftOffset;
      const comparedPosition = clickPosition / this.$refs.timeline.offsetWidth;
      const clickTime = comparedPosition * this.duration + this.start;

      /** move player to time */
      this.$emit("movePlayer", roundToMilliseconds(clickTime));
    },
    // @vuese
    // Update mouse position
    handleMouseMove(event) {
      const absoluteX = event.pageX;
      const { left } = this.$refs.timeline.getBoundingClientRect();
      this.mousePosition = absoluteX - left;
    },
    // @vuese
    // Update player cursor position
    syncPlayerCursor({ smooth = false } = {}) {
      if (this.isPlayerVisible) {
        this.$refs.playerCursor.moveTo(this.getTimePosition(this.playerTime), {
          smooth
        });
      }
    },
    // @vuese
    // Update live cursor position
    syncLiveCursor({ smooth = false } = {}) {
      if (this.isStreamEndVisible) {
        this.$refs.liveCursor.moveTo(1, {
          smooth
        });
      }
    },
    // @vuese
    // Check is this timestamp in [start:end] range
    // @arg time - UNIX timestamp
    checkTimeVisible(time) {
      return time != null && time >= this.start && time <= this.end;
    },
    // @vuese
    // Return time position on frame as (left padding / full width) ratio (in range [0-1])
    // @arg time - UNIX timestamp
    getTimePosition(time) {
      return (time - this.start) / this.duration;
    }
  }
};
</script>

<style lang="scss" scoped>
.timeline {
  background-color: transparent;
  &__scale {
    height: 30px;
  }
  &__thumbnails {
    position: relative;
    width: 100%;
    /** height: calc(100% - 30px); */
    height: 100%;
  }
  &__popup {
    position: absolute;
    width: 100%;
  }
}

.thumbnails {
  background-color: black;

  &__list {
    position: absolute;
    height: 100%;
    width: 100%;
    top: 0;
    left: 0;
  }
  &__slider {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
  }
  &__breaks {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
  }
}

.popup-wrapper {
  &__image {
    position: absolute;
    top: 0;
    transform: translateY(-100%);
  }
}
</style>
